import { useNavigate } from 'react-router-dom';
import { GAME_MODES } from '@blind-kings/game-core';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';

export type MatchHistoryGame = {
  gameId?: string;
  coins?: number;
  firstPlayerId?: string;
  secondPlayerId?: string;
  firstPlayerDisplayName?: string;
  secondPlayerDisplayName?: string;
  gameLength?: string;
  gameMode?: string;
  result?: string;
  winningType?: string;
  finishedAt?: number;
  xp?: number;
  eloRated?: boolean;
  eloDelta?: number;
  eloBefore?: number;
  eloAfter?: number;
};

export type MatchHistoryProps = {
  games?: MatchHistoryGame[];
  /** When embedded in Overview, drop outer page padding. */
  compact?: boolean;
};

function resultLabel(result?: string) {
  if (result === 'win' || result === 'Victory') return 'Victory';
  if (result === 'loss' || result === 'Defeat') return 'Defeat';
  if (result === 'abort') return 'Abort';
  return result ?? '';
}

function isDefeat(result?: string) {
  return result === 'loss' || result === 'Defeat';
}

export default function MatchHistory({ games = [], compact = false }: MatchHistoryProps) {
  const navigate = useNavigate();

  function handleGameTileClick(gameId?: string) {
    if (gameId) navigate(`/play/${gameId}`);
  }

  const list = (
    <div className={compact ? 'space-y-3' : 'space-y-3'}>
      {games.length ? (
        games.map(
          ({
            gameId,
            coins,
            firstPlayerDisplayName,
            secondPlayerDisplayName,
            gameLength,
            gameMode,
            result,
            winningType,
            finishedAt = Date.now(),
            xp,
            eloRated,
            eloDelta,
            eloAfter,
          }) => {
            const label = resultLabel(result);
            const eloLabel =
              gameMode === GAME_MODES.RANKED_GAME
                ? eloRated && eloDelta != null
                  ? `Elo ${eloDelta > 0 ? '+' : ''}${eloDelta}${
                      eloAfter != null ? ` → ${eloAfter}` : ''
                    }`
                  : 'Elo —'
                : null;
            return (
              <button
                type="button"
                key={gameId}
                className="relative flex h-28 w-full cursor-pointer items-center border border-bk-gold-dim/40 bg-bk-panel text-bk-text transition-colors hover:border-bk-gold hover:bg-bk-panel-raised"
                onClick={() => handleGameTileClick(gameId)}
              >
                <div
                  className={`flex h-full w-8 items-center justify-center ${
                    isDefeat(result) ? 'bg-bk-danger' : 'bg-bk-gold-dim'
                  }`}
                >
                  <div className="-rotate-90 text-[10px] font-bold uppercase tracking-wider text-bk-text">
                    {label}
                  </div>
                </div>
                <div className="flex h-full w-full items-end justify-between px-2 py-2 sm:px-4">
                  <div className="flex h-full flex-col items-center justify-center gap-1 text-sm sm:text-base md:text-lg">
                    {gameMode === GAME_MODES.LOCAL_GAME ? (
                      <div className="font-semibold uppercase tracking-wide text-bk-gold">
                        Local Game
                      </div>
                    ) : (
                      <>
                        <div>{firstPlayerDisplayName || 'Unknown'}</div>
                        <div className="font-bold text-bk-muted">VS</div>
                        <div>{secondPlayerDisplayName || 'Unknown'}</div>
                      </>
                    )}
                  </div>
                  <div className="absolute top-2 right-4 text-xs text-bk-muted">
                    {new Date(finishedAt).toLocaleString()}
                  </div>
                  <div className="flex flex-wrap gap-1 px-1 md:gap-2 md:px-2">
                    {(
                      [
                        ['Mode', gameMode],
                        ['Length', gameLength],
                        ['Won By', winningType],
                        ['XP', xp],
                        ['Coins', coins],
                        ...(eloLabel ? [['Elo', eloLabel]] : []),
                      ] as [string, string | number | undefined][]
                    )
                      .filter(([, value]) => value !== undefined && value !== '')
                      .map(([name, value]) => (
                        <div
                          key={name}
                          className={[
                            'flex border border-bk-border px-1 text-xs md:px-2 md:py-1 md:text-sm',
                            name === 'XP'
                              ? 'border-bk-accent/50 text-sky-300'
                              : name === 'Coins' || name === 'Elo'
                                ? 'border-bk-gold-dim text-bk-gold'
                                : 'text-bk-muted',
                          ].join(' ')}
                        >
                          <span className="mr-2 hidden md:block">{name}: </span>
                          <span>
                            {name === 'Elo'
                              ? String(value)
                              : `${value}`.toLowerCase().replace(/_/g, ' ')}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </button>
            );
          }
        )
      ) : (
        <BkPanel className="flex flex-col items-center justify-center gap-2 py-12 text-center">
          <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold">
            No Games Found
          </h1>
          <p className="text-bk-muted">
            It looks like the user hasn&apos;t played any games yet
          </p>
        </BkPanel>
      )}
    </div>
  );

  if (compact) return list;
  return <BkPage className="space-y-3">{list}</BkPage>;
}
